import {
  CustomTable,
} from "../../../shared";
import React from "react";
import {
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputControl from "../../../shared/components/InputControl";
import { useParams } from "react-router-dom";
import { SendEmailsDialog } from "../components";
import { useClientAccountTransactions } from "../hooks/useClientAccountTransactions";
import { Telegram } from "@mui/icons-material";
import { DashBoardDateRangePicker } from "../../Dashboard";
import { formatAmount } from "../../../utils";

export const ClientAccountTransactions = ({ invoiceEmails }) => {
  const { id } = useParams();

  const {
    state,
    isStoreAdmin,
    navigate,
    handleGetSelectedRows,
    handleModal,
    handleSendInvoice,
    handleFilter,
    handleSearch,
    onSeachClear,
    handleDateChangeApply,
    onChangeDate,
    handleIsOpen,
    resetFilters,
    handleApplyFilter,
    isCheckIndeterminate,
    isAllChecked,
    handleCheckAll,
  } = useClientAccountTransactions({ id: id, isList: true });


  const {
    isLoading,
    dataList,
    selectedTransactions,
    openSendInvoiceDialog,
    filter
  } = state


  const transactionType = [
    { label: "Payment", value: "Payment" },
    { label: "Purchase", value: "Purchase" },
    { label: "Refund", value: "Refund" },
    { label: 'Payment Reverted', value: 'Payment Reverted' },
    { label: 'Brought Forward', value: 'Brought Forward' }
  ];

  const columns = React.useMemo(
    () => [
      {
        id: "select",
        padding: "checkbox",
        disableSortBy: true,
        // Add the Header component to provide "Select All" functionality
        Header: ({ getToggleAllRowsSelectedProps, rows }) => {
          // Only enable if there are purchase transactions to select
          const hasPurchaseRows = rows.some(row => row.original.transaction_type === "Purchase");

          return (
            <Stack justifyContent="center" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    classes={{ root: "custom-checkbox-root" }}
                    onClick={(e) => e.stopPropagation()}
                    indeterminate={isCheckIndeterminate(rows)}
                    checked={isAllChecked(rows)}
                    onChange={(e) => {
                      handleCheckAll(e.target.checked, rows);
                    }}
                    disabled={!hasPurchaseRows}
                    color="primary"
                  />
                }
              />
            </Stack>
          );
        },
        Cell: ({ row }) => {
          let disable = row.original.transaction_type === "Purchase" ? false : true;
          return (
            <Stack justifyContent="end">
              <FormControlLabel
                control={
                  <Checkbox
                    classes={{ root: "custom-checkbox-root" }}
                    onChange={(e) => {
                      handleGetSelectedRows(e.target.checked, row.original);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    indeterminate={false}
                    disabled={disable}
                    checked={selectedTransactions.some(t => t === row.original.transaction_id)}
                    color="primary"
                  />
                }
              />
            </Stack>
          )
        },
      },
      {
        Header: "TXN ID",
        accessor: "transaction_id",
      },
      {
        Header: "Order Number",
        accessor: "order_number",
      },
      {
        Header: "TXN Date ",
        accessor: (val) => {
          return (
            <Typography variant="subtitle2" fontWeight={400} color="#636363">
              {val.transaction_date}
            </Typography>
          );
        },
      },
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ value, row }) => {
          let settlement_balance = row.original.settlement_balance ? row.original.settlement_balance : 0;
          return <Stack
            sx={{ flexDirection: "row", fontWeight: 500, gap: "4px" }}
          >
            <span>
              ${value?.toFixed(2)}
            </span>
            {row.original.transaction_type === "Purchase" && !!settlement_balance && (
              <Box sx={{
                color: "#C07302",
                marginLeft: "16px",
              }}>
                Due  {formatAmount(settlement_balance)}
              </Box>
            )}
          </Stack>;
        },
      },
      {
        Header: "Running Bal",
        accessor: "running_balance",
        Cell: ({ value, row }) => {
          if (value === 0 && !row.original.transaction_type) {
            return <div>--</div>;
          }
          return <div>{formatAmount(value)}</div>;
        },
      },
      {
        Header: "TXN Type",
        accessor: "transaction_type",
        Cell: ({ value, row }) => {
          // Determine transaction type if not provided
          let displayType = value;

          if (!displayType) {
            const original = row.original;
            if (original.type === "credit" && original.order_id) {
              displayType = "Purchase";
            } else if (original.type === "debit" && original.refund_id) {
              displayType = "Refund";
            } else if (original.type === "debit") {
              displayType = "Payment";
            }
          }

          // Handle when still no value after determination
          if (!displayType) {
            return <div>--</div>;
          }

          return (
            <>
              <Chip
                label={displayType}
                color={
                  displayType === "Payment"
                    ? "success"
                    : displayType === "Purchase"
                      ? "primary"
                      : displayType === "Payment Reverted"
                        ? "warning"
                        : "error"
                }
                variant="outlined"
                size="small"
                sx={{
                  border: "none !important",
                  color: displayType === "Payment Reverted" && "#C07302",
                  backgroundColor:
                    displayType === "Purchase"
                      ? "rgba(21, 96, 212, 0.08)"
                      : displayType === "Payment Reverted"
                        ? "#FFF4CE"
                        : "inherit",
                }}
              />
            </>
          );
        },
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        disableGlobalFilter: true,
        accessor: (row, index) => {
          return row.type === "credit" && !row.order_id ? null : (
            <ActionButtons row={row} />
          );
        },
      },
    ],
    [selectedTransactions] // Add this dependency to re-render when selections change
  );

  const ActionButtons = ({ row }) => {
    return (
      <div className="action-buttons" onClick={(e) => e.preventDefault()}>
        {handleNavigation(row) !== null && (
          <img
            alt="details"
            onClick={() => {
              navigate(handleNavigation(row));
            }}
            src="/icons/arrow_forward.svg"
          />
        )}
      </div>
    );
  };

  const handleNavigation = (e) => {
    let link = null;
    const baseUrl = isStoreAdmin ? "/store-admin" : "";

    // Determine transaction type if not provided
    let transactionType = e.transaction_type;

    if (!transactionType) {
      if (e.type === "credit" && e.order_id) {
        transactionType = "Purchase";
      } else if (e.type === "debit" && e.refund_id) {
        transactionType = "Refund";
      } else if (e.type === "debit") {
        transactionType = "Payment";
      }
    }

    switch (transactionType) {
      case "Payment":
        link = `${baseUrl}/client-account-v2/details/${id}/transactions/${e.transaction_id}`;
        break;
      case "Purchase":
        if (e.order_id) {
          link = `${baseUrl}/client-account-v2/details/${id}/purchace-details/${e.order_id}`;
        }
        break;
      case "Refund":
        if (e.refund_id) {
          link = `${baseUrl}/client-account-v2/details/${id}/refund-details/${e.refund_id}`;
        }
        break;
      case "Payment Reverted":
        if (e.transaction_id) {
          link = `${baseUrl}/client-account-v2/details/${id}/transactions/${e.transaction_id}`;
        }
        break;
      case "Brought Forward":
        if (e.transaction_id) {
          link = `${baseUrl}/client-account-v2/details/${id}/transactions/${e.transaction_id}`;
        }
        break;
      default:
        link = null;
    }
    return link;
  };


  return (
    <>

      <Stack Stack={3}>
        <CustomTable
          columns={columns}
          data={dataList}
          isLoading={isLoading}
          headerRightContent={
            <Button
              startIcon={<Telegram />}
              onClick={() => handleModal("sendInvoice", true)}
              disabled={selectedTransactions.length === 0}
            >
              Send Invoice
            </Button>
          }
          defaultSearch={false}
          filterContent={
            <Stack flexDirection="row" gap={2} width="100%" alignItems="center">
              <InputControl
                type="table-search"
                value={filter.search}
                onChange={(e) => handleSearch(e.target.value)}
                onClear={onSeachClear}
              />

              <InputControl
                type="select"
                name="transaction_type"
                label="Transaction Type"
                placeholder="Select Transaction Type"
                optionValue={transactionType}
                value={filter.transactionType ?? ""}
                onChange={(e) => {
                  console.log(e);

                  handleFilter(e.target.value, "transactionType");
                }
                }
                width="400px"
              />
              <DashBoardDateRangePicker
                onChangeDate={onChangeDate}
                isList={true}
                range={state.date}
                showRange={state.showDate}
                isOpen={state.isCalenderOpen}
                handleIsOpen={handleIsOpen}
                handleDateChangeApply={handleDateChangeApply}
                position="left"
              />
              <Button sx={{
                height: 40,
              }}
                variant="contained"
                onClick={() => handleApplyFilter()}
              >
                Apply
              </Button>
              <Button
                type="button"
                size="medium"
                variant="text"
                color="gray"
                sx={{
                  minWidth: "40px",
                  p: "8px",
                }}
                onClick={() => resetFilters()}
              >
                <CloseIcon />
              </Button>
            </Stack>
          }
          tableTitle="Transactions"
          onRowLink={(e) => {
            return handleNavigation(e);
          }}
        />
      </Stack >

      <SendEmailsDialog
        open={openSendInvoiceDialog}
        key={openSendInvoiceDialog}
        disableCloseOnBackgroundClick
        handleClose={() => handleModal("sendInvoice", false)}
        helperText="The account statement will be sent to"
        submitting={false} //send button loading
        defaultEmails={invoiceEmails ?? []} //default emails
        handleFormSubmit={handleSendInvoice}
      />
    </>
  );
};
